import React from "react";
import { Dialog } from "../../../ui/Dialog";
import { Button } from "../../../ui/Button";
import type { Evaluator } from "../types";

interface DeleteEvaluatorDialogProps {
  evaluator: Evaluator;
  /** Number of stored versions, or null while history is still loading. */
  versionCount: number | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for deleting an evaluator.
 *
 * Replaces a native `window.confirm`, which was off-brand and — more importantly — named
 * no consequence. Deleting marks the active version deleted, so `get_latest_by_group_id`
 * stops resolving it and any request naming this evaluator starts returning 404, while
 * evaluations already scored keep their scores.
 */
export function DeleteEvaluatorDialog({
  evaluator,
  versionCount,
  isSubmitting,
  onCancel,
  onConfirm,
}: DeleteEvaluatorDialogProps) {
  return (
    <Dialog
      title="Delete this evaluator?"
      subtitle={<span className="font-mono">{evaluator.group_id}</span>}
      onClose={onCancel}
      maxWidth="max-w-lg"
      maxHeight="max-h-[70vh]"
      ariaLabelledBy="delete-evaluator-title"
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs font-medium text-muted-foreground mb-1.5">
            Current criterion
          </p>
          <p className="font-mono text-sm text-foreground break-words">
            {evaluator.criteria}
          </p>
        </div>

        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          <li>
            Any API request naming{" "}
            <span className="font-mono text-foreground">{evaluator.group_id}</span> will
            start failing.
          </li>
          <li>
            Evaluations already scored by it keep their scores, and stay on your Insights
            charts.
          </li>
          {versionCount !== null && versionCount > 1 && (
            <li>
              All {versionCount} stored versions are retired. The name cannot be reused —{" "}
              <span className="font-mono text-foreground">group_id</span> is immutable.
            </li>
          )}
        </ul>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting…" : "Delete evaluator"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
